import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/frontend-plugin-api';
import type { CompoundEntityRef } from '@backstage/catalog-model';

export const SLUG_ANNOTATION = 'code.overheid.nl/project-slug';

export type PullRequest = {
  number: number;
  title: string;
  author: string;
  draft: boolean;
  updatedAt: string;
  url: string;
};

export type WorkflowRun = {
  id: number;
  title: string;
  workflow: string;
  branch: string;
  status: string;
  createdAt: string;
  url: string;
};

export type RepositoryActivity = {
  repositoryUrl: string;
  visible: boolean;
  pullRequests: PullRequest[];
  runs: WorkflowRun[];
  checks: { label: string; present: boolean; fix?: string; proposal?: string }[];
  packages: { name: string; type: string; version: string; createdAt: string; url: string }[];
};

/** One call per repository; the backend reads several things from code.overheid.nl for each. */
export async function fetchActivity(
  baseUrl: string,
  fetch: typeof window.fetch,
  { namespace, kind, name }: CompoundEntityRef,
): Promise<RepositoryActivity> {
  const response = await fetch(`${baseUrl}/entity/${namespace}/${kind}/${name}/activity`);
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new Error(body?.error?.message ?? 'code.overheid.nl geeft nu geen antwoord.');
  }
  return response.json();
}

export function useRepositoryActivity(ref: CompoundEntityRef) {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [activity, setActivity] = useState<RepositoryActivity>();
  const [failure, setFailure] = useState<string>();
  const { namespace, kind, name } = ref;

  useEffect(() => {
    let current = true;
    (async () => {
      try {
        const base = await discovery.getBaseUrl('forgejo');
        const result = await fetchActivity(base, fetch, { namespace, kind, name });
        if (current) {
          setActivity(result);
        }
      } catch (error: any) {
        if (current) {
          setFailure(error.message);
        }
      }
    })();
    return () => {
      current = false;
    };
  }, [discovery, fetch, namespace, kind, name]);

  return { activity, failure };
}

export type MyTeam = {
  entityRef: string;
  title: string;
  organisation: string;
  organisationTitle: string;
  team: string;
  description?: string;
  codeUrl: string;
};

export type MyOrganisation = {
  name: string;
  title: string;
  isOwner: boolean;
  isMember: boolean;
  codeUrl: string;
};

export type MyTeams = {
  teams: MyTeam[];
  organisations: MyOrganisation[];
  /** Organisations of the person that the bot is not in, as far as code.overheid.nl shows them. */
  hidden: { name: string; title: string; addBotUrl: string }[];
  bot: { login: string; organisationsUrl: string };
};

/**
 * The teams of the person who is signed in, live from code.overheid.nl. The backend writes any
 * difference to the catalog on the way, so pages that read the catalog catch up straight after.
 */
export async function fetchMyTeams(baseUrl: string, fetch: typeof window.fetch): Promise<MyTeams> {
  const response = await fetch(`${baseUrl}/me/teams`);
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new Error(body?.error?.message ?? 'code.overheid.nl geeft nu geen antwoord.');
  }
  return response.json();
}

export function useMyTeams() {
  const discovery = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [myTeams, setMyTeams] = useState<MyTeams>();
  const [failure, setFailure] = useState<string>();

  useEffect(() => {
    let current = true;
    discovery
      .getBaseUrl('forgejo')
      .then(baseUrl => fetchMyTeams(baseUrl, fetch))
      .then(result => current && setMyTeams(result))
      .catch((error: Error) => current && setFailure(error.message));
    return () => {
      current = false;
    };
  }, [discovery, fetch]);

  return { myTeams, failure };
}
